import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Skeleton from '@mui/material/Skeleton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { styles } from '../WorkingHours.styles';
import { ProductionHoursMatrixEntry } from '../utils/productionHoursApiTypes';
import { systemGroupTypeLabelFromApi, SYSTEM_GROUP_TYPE_VALUES } from '../utils/productionHoursEnumMaps';
import { UserSystemGroup } from 'store/storeTypes';

// Working Hours does not apply to DTF systems - never shown as a matrix column.
const DTF_TYPE_VALUE = SYSTEM_GROUP_TYPE_VALUES[UserSystemGroup.DTF];

type ProductionHoursMatrixProps = {
  entries: ProductionHoursMatrixEntry[];
  currentSiteName: string;
  isLoading: boolean;
};

const renderCellStatus = (isSet: boolean) => {
  if (isSet) {
    return (
      <Box sx={{ ...styles.matrixCell, ...styles.matrixCellSet }}>
        <Box component="span" sx={styles.matrixSetIcon}>
          ✓
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ ...styles.matrixCell, ...styles.matrixCellUnset }}>
      <Box component="span" sx={styles.matrixUnsetIcon}>
        –
      </Box>
    </Box>
  );
};

const ProductionHoursMatrix = ({ entries, currentSiteName, isLoading }: ProductionHoursMatrixProps) => {
  // 0 ("None") is the enum's unset/default value, not a real system type — never a column.
  // DTF is excluded - Working Hours does not apply to DTF systems.
  const columns = useMemo(
    () =>
      Array.from(
        new Set(
          entries
            .map(entry => entry.systemGroupType)
            .filter(type => type && type !== DTF_TYPE_VALUE)
        )
      ).map(type => ({ value: type, label: systemGroupTypeLabelFromApi(type) })),
    [entries]
  );

  const rows = useMemo(() => {
    const bySite = new Map<string, { siteName: string; bySystemGroupType: Record<number, boolean> }>();
    entries
      .filter(entry => entry.systemGroupType !== DTF_TYPE_VALUE)
      .forEach(entry => {
        if (!bySite.has(entry.siteName)) {
          bySite.set(entry.siteName, { siteName: entry.siteName, bySystemGroupType: {} });
        }
        bySite.get(entry.siteName)!.bySystemGroupType[entry.systemGroupType] = entry.isSet;
      });
    return Array.from(bySite.values());
  }, [entries]);

  return (
    <Box sx={styles.matrixWrapper}>
      <Box sx={styles.matrixHeader}>
        <Typography sx={styles.matrixTitle}>Configuration matrix</Typography>
        <InfoOutlinedIcon sx={styles.matrixInfoIcon} />
      </Box>

      {isLoading ? (
        <Box>
          {[0, 1, 2, 3].map(i => (
            <Skeleton key={i} variant="rounded" height={30} sx={{ marginBottom: 0.75 }} />
          ))}
        </Box>
      ) : rows.length === 0 ? (
        <Typography sx={styles.matrixFootnote}>No configuration data yet.</Typography>
      ) : (
        <Table size="small" sx={styles.matrixTable}>
          <TableHead>
            <TableRow>
              <TableCell sx={styles.matrixHeadSite}>Site</TableCell>
              {columns.map(column => (
                <TableCell key={column.value} align="center" sx={styles.matrixHeadCell}>
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.siteName}>
                <TableCell sx={styles.matrixSiteCell}>
                  <Typography sx={styles.matrixSiteText}>{row.siteName}</Typography>
                  {row.siteName === currentSiteName && (
                    <Typography sx={styles.matrixCurrentMark}>Current</Typography>
                  )}
                </TableCell>
                {columns.map(column => (
                  <TableCell key={column.value} align="center" sx={styles.matrixBodyCell}>
                    {renderCellStatus(!!row.bySystemGroupType[column.value])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Box sx={styles.matrixLegend}>
        <Box sx={styles.matrixLegendItem}>
          <Box sx={{ ...styles.matrixCell, ...styles.matrixCellSet, width: 18, height: 18 }}>✓</Box>
          <Typography sx={styles.matrixLegendText}>Hours are set</Typography>
        </Box>
        <Box sx={styles.matrixLegendItem}>
          <Box sx={{ ...styles.matrixCell, ...styles.matrixCellUnset, width: 18, height: 18 }}>–</Box>
          <Typography sx={styles.matrixLegendText}>Not set</Typography>
        </Box>
      </Box>

      <Typography sx={styles.matrixFootnote}>
        Unset cells fall back to 24 h in KPI calculations.
      </Typography>
    </Box>
  );
};

export default ProductionHoursMatrix;
